import React from 'react';

import Link from 'next/link';
import Meta from '../components/Meta';
import HeaderSection from "../components/header/HeaderSection";
import Button from "../components/button/Button";

const nowReading = {
  title: 'AI Agents in Action',
  author: 'Micheal Lanham',
  image: '/images/books/ai-agents-in-action.jpg',
  description: 'A hands-on guide to building AI agents and multi-agent systems using large language models. Micheal Lanham walks through practical patterns for designing, building, and deploying autonomous AI agents that can reason, plan, and act in the real world.',
};

const upNext = [
  { title: 'Good Strategy, Bad Strategy', author: 'Richard P. Rumelt' },
  { title: 'The Performance Pipeline', author: 'Stephen Drotter' },
];

const previouslyRead = [
  { title: 'Tidy First?', author: 'Kent Beck', image: '/images/books/tidy-first.jpg', date: 'June 2026', link: '/books/tidy-first' },
  { title: 'Architecture Modernization', author: 'Nick Tune', image: '/images/books/architecture-modernization.jpg', date: 'October 2025', link: '/books/architecture-modernization' },
  { title: 'Team Topologies', author: 'Matthew Skelton, Manuel Pais', image: '/images/books/team-topologies.jpg', date: 'September 2025', link: '/books/team-topologies' },
  { title: 'AI Engineering', author: 'Chip Huyen', image: '/images/books/ai-engineering.jpg', date: 'June 2025', link: '/books/ai-engineering' },
  { title: 'Desgining Data Intensive Applications', author: '', image: '/images/books/designing-data-intensive-applications.jpg', date: 'January 2025', link: '/books/designing-data-intensive-applications' },
  { title: 'No Rules Rules', author: 'Reed Hastings, Erin Meyer', image: '/images/books/no-rules-rules.jpg', date: 'January 2025', link: '/books/no-rules-rules' },
  { title: 'The Art of Readable Code', author: 'Dustin Boswell, Trevor Foucher', image: '/images/books/the-art-of-readable-code.jpg', date: 'October 2024', link: '/books/the-art-of-readable-code' },
  { title: 'The Staff Engineer\'s Path', author: 'Tanya Reilly', image: '/images/books/the-staff-engineers-path.jpg', date: 'July 2024', link: '/books/the-staff-engineers-path' },
  { title: 'System Design Interview', author: 'Alex Xu', image: '/images/books/system-design-interview.jpg', date: 'May 2024', link: '/books/system-design-interview' },
  { title: 'Grokking Concurrency', author: 'Kirill Bobrov', image: '/images/books/grokking-concurrency.jpg', date: 'April 2024', link: '/books/grokking-concurrency' },
  { title: 'Escaping the Build Trap', author: 'Melissa Perry', image: '/images/books/escaping-the-build-trap.jpg', date: 'March 2024', link: '/books/escaping-the-build-trap' },
  { title: 'Understanding Distributed Systems', author: 'Roberto Vitillo', image: '/images/books/understanding-distributed-systems.jpg', date: 'January 2024', link: '/books/understanding-distributed-systems' },
  { title: 'The New Turing Omnibus', author: 'A.K. Dewdney', image: '/images/books/the-new-turing-omnibus.jpg', date: 'January 2024', link: '/books/the-new-turing-omnibus' },
  { title: 'Database Internals', author: 'Alex Petrov', image: '/images/books/database-internals.jpg', date: 'August 2023', link: '/books/database-internals' },
  { title: 'Engineering Management for the Rest of Us', author: 'Sarah Drasner', image: '/images/books/engineering-management-for-the-rest-of-us.jpg', date: 'July 2023', link: '/books/engineering-management-for-the-rest-of-us' },
  { title: 'A Philosophy of Software Design', author: 'John Ousterhout', image: '/images/books/a-philosophy-of-software-design.jpg', date: 'May 2023', link: '/books/a-philosophy-of-software-design' },
];

const Page = () => {
  return (
    <>
      <Meta title="Currently Reading - The Engineering Book Club"/>
      <HeaderSection title="Currently Reading" subtitle="Follow along with what we're reading"/>

      <section className="relative py-1 md:pt-8">
        <div className="container">
          <div className="mx-auto">

            <h2 className="text-jacarta-700 font-display mb-6 text-3xl dark:text-white">Now Reading</h2>
            <div className="bg-jacarta-50 dark:bg-jacarta-700 rounded-2.5xl p-8 mb-10 lg:flex lg:items-center lg:gap-10">
              <div className="lg:w-1/4 mb-6 lg:mb-0 flex justify-center">
                <img
                  src={nowReading.image}
                  alt={nowReading.title}
                  className="rounded-lg shadow-lg max-h-80 object-contain"
                />
              </div>
              <div className="lg:w-3/4">
                <h3 className="text-jacarta-700 font-display text-2xl dark:text-white mb-2">{nowReading.title}</h3>
                <p className="text-accent mb-4 text-lg">by {nowReading.author}</p>
                <p className="dark:text-jacarta-60 leading-normal">{nowReading.description}</p>
              </div>
            </div>

            <h2 className="text-jacarta-700 font-display mb-6 text-3xl dark:text-white">Up Next</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {upNext.map((book, index) => (
                <div key={index} className="bg-jacarta-50 dark:bg-jacarta-700 rounded-2.5xl p-6">
                  <span className="text-accent font-display text-sm mb-2 block">#{index + 1}</span>
                  <h3 className="text-jacarta-700 font-display text-lg dark:text-white mb-1">{book.title}</h3>
                  <p className="dark:text-jacarta-60 text-sm">by {book.author}</p>
                </div>
              ))}
            </div>

            <h2 className="text-jacarta-700 font-display mb-6 text-3xl dark:text-white">Previously Read</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              {previouslyRead.map((book, index) => (
                <Link href={book.link} key={index} className="text-center block hover:opacity-80 transition-opacity">
                  <img
                    src={book.image}
                    alt={book.title}
                    className="rounded-lg shadow-md mb-3 w-full object-cover"
                  />
                  <h4 className="text-jacarta-700 font-display text-sm dark:text-white mb-1">{book.title}</h4>
                  <p className="dark:text-jacarta-60 text-xs">{book.author}</p>
                  <p className="text-accent text-xs mt-1">{book.date}</p>
                </Link>
              ))}
            </div>

          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <p className="dark:text-jacarta-60 mb-4 leading-normal text-center">
            Want to read along with us?
          </p>
          <div className="mx-auto max-w-2xl pt-4 text-center">
            <Button title="Join Us Now" link="/join-now"/>
          </div>
        </div>
      </section>
    </>
  );
};

export default Page;
